/**
 * CR173: Preview Debug Panel
 * 
 * Displays real-time debug information about the preview config store:
 * - Last update timestamp
 * - Source of last update
 * - Current version/hash
 * - Blocked updates / validation issues
 */

import { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Bug, ChevronDown, RefreshCw, Clock, Layers, Palette, Layout, CreditCard } from "lucide-react";
import { usePreviewDebug } from "@/hooks/usePreviewConfig";
import { cn } from "@/lib/utils";

interface PreviewDebugPanelProps {
  className?: string;
  defaultOpen?: boolean;
}

export function PreviewDebugPanel({ className, defaultOpen = false }: PreviewDebugPanelProps) {
  const { version, lastUpdatedAt, lastUpdatedBy, snapshot } = usePreviewDebug();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [updateFlash, setUpdateFlash] = useState(false);
  
  // Flash effect on updates
  useEffect(() => {
    setUpdateFlash(true);
    const timer = setTimeout(() => setUpdateFlash(false), 300);
    return () => clearTimeout(timer);
  }, [version]);
  
  const formatTime = useCallback((timestamp: number) => {
    const date = new Date(timestamp);
    const ms = date.getMilliseconds().toString().padStart(3, "0");
    return `${date.toLocaleTimeString("en-US", { 
      hour12: false, 
      hour: "2-digit", 
      minute: "2-digit", 
      second: "2-digit",
    })}.${ms}`;
  }, []);
  
  const getSourceIcon = (source: string) => {
    switch (source) {
      case "theme_labs": return <Palette className="h-3 w-3" />;
      case "layout_studio": return <Layout className="h-3 w-3" />;
      case "deal_cards": return <CreditCard className="h-3 w-3" />;
      case "preview_controls": return <Layers className="h-3 w-3" />;
      default: return <RefreshCw className="h-3 w-3" />;
    }
  };
  
  const getSourceColor = (source: string) => {
    switch (source) {
      case "theme_labs": return "bg-purple-500/20 text-purple-700 border-purple-300";
      case "layout_studio": return "bg-blue-500/20 text-blue-700 border-blue-300";
      case "deal_cards": return "bg-green-500/20 text-green-700 border-green-300";
      case "preview_controls": return "bg-orange-500/20 text-orange-700 border-orange-300";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={className}>
      <CollapsibleTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className={cn(
            "h-6 px-2 text-[10px] text-muted-foreground transition-colors",
            updateFlash && "bg-primary/10"
          )}
        >
          <Bug className="h-3 w-3 mr-1" />
          CR173 Debug
          <Badge 
            variant="outline" 
            className={cn(
              "ml-1.5 h-4 text-[9px] px-1 transition-all",
              updateFlash && "ring-2 ring-primary/50"
            )}
          >
            v{version}
          </Badge>
          <ChevronDown className={cn("h-3 w-3 ml-1 transition-transform", isOpen && "rotate-180")} />
        </Button>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="mt-2">
        <div className="p-2 bg-muted/50 rounded text-[10px] font-mono space-y-1.5 max-h-48 overflow-auto border">
          {/* Last Update Info */}
          <div className="flex items-center gap-2">
            <Clock className="h-3 w-3 text-muted-foreground" />
            <span className="text-muted-foreground">Last update:</span>
            <span className={cn("font-medium", updateFlash && "text-primary")}>
              {formatTime(lastUpdatedAt)}
            </span>
          </div>
          
          {/* Update Source */}
          <div className="flex items-center gap-2">
            {getSourceIcon(lastUpdatedBy)}
            <span className="text-muted-foreground">Source:</span>
            <Badge 
              variant="outline" 
              className={cn("h-4 text-[9px] px-1.5", getSourceColor(lastUpdatedBy))}
            >
              {lastUpdatedBy.replace(/_/g, " ")}
            </Badge>
          </div>
          
          {/* Config State */}
          <div className="border-t pt-1.5 mt-1.5 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Global Theme:</span>
              <Badge variant={snapshot.hasGlobalTheme ? "default" : "secondary"} className="h-4 text-[9px] px-1">
                {snapshot.hasGlobalTheme ? "✓ Set" : "— Default"}
              </Badge>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Layout:</span>
              <Badge variant={snapshot.hasLayout ? "default" : "secondary"} className="h-4 text-[9px] px-1">
                {snapshot.hasLayout ? "✓ Set" : "— Default"}
              </Badge>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Deal Card UX:</span>
              <Badge variant={snapshot.hasDealCardUX ? "default" : "secondary"} className="h-4 text-[9px] px-1">
                {snapshot.hasDealCardUX ? "✓ Set" : "— Default"}
              </Badge>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Type Overrides:</span>
              <Badge variant="outline" className="h-4 text-[9px] px-1">
                {snapshot.dealTypeOverrideCount} types
              </Badge>
            </div>
          </div>
          
          {/* Preview State */}
          <div className="border-t pt-1.5 mt-1.5 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Preview Source:</span>
              <span className="font-medium">{snapshot.previewSource}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Viewport:</span>
              <span className="font-medium">{snapshot.viewport}</span>
            </div>
            
            {snapshot.activeDealType && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Active Type:</span>
                <span className="font-medium">{snapshot.activeDealType}</span>
              </div>
            )}
          </div>
          
          {/* Hash */}
          <div className="border-t pt-1.5 mt-1.5">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Config Hash:</span>
              <code className="text-[9px] bg-background px-1 py-0.5 rounded">
                {`v${version}-${lastUpdatedBy.slice(0, 3)}`}
              </code>
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default PreviewDebugPanel;
